// KanbanBoard — 4-column kanban for the Wishes.tsx route.
//
// Composes:
//   - KanbanColumn x4 (pending / in_progress / completed / abandoned)
//   - KanbanCard xN (one per wish in the column)
//   - Drops into a `DndContext` owned by `Wishes.tsx`; this component is
//     a pure presentational wrapper that groups + sorts wishes and lays
//     out the columns.
//
// Visual rhythm per `docs/spec/wishlist/DESIGN.md §5.1`:
//   - 4 equal-width columns at desktop (≥ 1024px)
//   - mobile: 4 collapsible sections (per W33.2) — for W33.1 the desktop
//     path is the primary deliverable; mobile rendering is the route's
//     reduced fallback (single column + status filter chip).
//   - soft WIP badge: when in_progress count > 5, "建議先完成手上的" badge
//     appears on the in_progress column (Q-new-2 a, soft warning)
//
// Sort: per DESIGN §5.2 — `-priority then -target_date asc`. We sort in
// JavaScript to avoid relying on a stable SQL sort (the backend orders by
// created_at DESC by default per API_REFERENCE §3.1).

import { useMemo } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard } from "./KanbanCard";
import type { WishStatus, WishSummary } from "@/lib/api/wishlist";
import { WISH_STATUS_LABEL, WISH_STATUS_ORDER } from "@/lib/i18n/wishlist";

// Per DESIGN §5.4 visual order; must match the `<option>` ordering in any
// status filter chip. W53.1 D-6: the sequence now lives beside the labels in
// `lib/i18n/wishlist.ts` instead of being retyped by every consumer.
export const KANBAN_STATUS_ORDER: readonly WishStatus[] = WISH_STATUS_ORDER;

const KANBAN_LABEL: Record<WishStatus, string> = WISH_STATUS_LABEL;

const KANBAN_WIP_THRESHOLD = 5;
const KANBAN_WIP_TEXT = "建議先完成手上的";

export interface KanbanBoardProps {
  wishes: WishSummary[];
  /** Today date (ISO YYYY-MM-DD) for footer countdown. */
  today: string;
  /** When false, render cards as read-only (no drag). */
  draggable?: boolean;
  /** Optional click → wish detail (W33.1 wires navigation). */
  onCardClick?: (wish: WishSummary) => void;
  /** Optional "New wish" button (top-right of header). */
  onNewWish?: () => void;
  className?: string;
}

export function KanbanBoard({
  wishes,
  today,
  draggable = true,
  onCardClick,
  onNewWish,
  className,
}: KanbanBoardProps) {
  // Group by status (stable order)
  const grouped = useMemo<Record<WishStatus, WishSummary[]>>(() => {
    const out: Record<WishStatus, WishSummary[]> = {
      pending: [],
      in_progress: [],
      completed: [],
      abandoned: [],
    };
    for (const w of wishes) {
      // Defensive: unknown statuses land in pending so the column never
      // silently swallows a wish.
      //
      // A row that is not an object at all is dropped, which is a different
      // case from an unknown status. `items: [null]` is a 200 response the API
      // layer passes through unvalidated, and this loop then threw
      // `Cannot read properties of null (reading 'status')`.
      //
      // This is the **third** copy of this loop — `Wishes.tsx` (mobile) and
      // `WishPeakCard` got the same guard first. It went unnoticed until a jsdom
      // test rendered the desktop board, because every audit in this repo runs at
      // 390px and this component never renders there.
      if (!w || typeof w !== "object" || typeof w.status !== "string") continue;
      const bucket = out[w.status as keyof typeof out] ?? out.pending;
      bucket.push(w);
    }
    // Sort each column: -priority then -target_date asc (DESIGN §5.2)
    for (const status of KANBAN_STATUS_ORDER) {
      out[status].sort((a, b) => {
        const pri = priorityRank(b.priority) - priorityRank(a.priority);
        if (pri !== 0) return pri;
        // earlier target_date first; nulls last
        if (a.target_date && b.target_date)
          return a.target_date.localeCompare(b.target_date);
        if (a.target_date) return -1;
        if (b.target_date) return 1;
        return 0;
      });
    }
    return out;
  }, [wishes]);

  const inProgressCount = grouped.in_progress.length;
  const wipBadge =
    inProgressCount > KANBAN_WIP_THRESHOLD
      ? `${KANBAN_WIP_TEXT} (${inProgressCount})`
      : undefined;

  return (
    <section
      data-component="KanbanBoard"
      data-testid="kanban-board"
      className={cn("flex flex-col gap-s-3", className)}
    >
      <header className="flex items-center justify-between gap-s-3">
        <h2 className="font-display text-title text-ink-1">心願板</h2>
        {onNewWish ? (
          <button
            type="button"
            onClick={onNewWish}
            data-testid="kanban-new-wish"
            // `h-11` rather than `h-9`: a 36px button on a pointer-driven
            // desktop layout is still a 36px target, and it was the one control
            // on the kanban board under the floor. The visual weight of the
            // pill is carried by its fill and radius, not by 8px of height.
            className="press inline-flex items-center gap-1.5 px-s-4 h-11 rounded-full bg-ink-1 text-paper text-body-sm font-semibold"
          >
            <Plus className="h-4 w-4" aria-hidden />
            新增心願
          </button>
        ) : null}
      </header>
      <div
        data-component="KanbanColumnRow"
        data-testid="kanban-column-row"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-s-3"
      >
        {KANBAN_STATUS_ORDER.map((status) => {
          const items = grouped[status];
          return (
            <KanbanColumn
              key={status}
              status={status}
              label={KANBAN_LABEL[status]}
              count={items.length}
              wipBadge={status === "in_progress" ? wipBadge : undefined}
            >
              {items.map((w) => (
                <KanbanCard
                  key={w.id}
                  wish={w}
                  today={today}
                  draggable={draggable}
                  onClick={onCardClick ? () => onCardClick(w) : undefined}
                />
              ))}
            </KanbanColumn>
          );
        })}
      </div>
    </section>
  );
}

function priorityRank(p: "must" | "want" | "maybe"): number {
  if (p === "must") return 2;
  if (p === "want") return 1;
  return 0;
}
